import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import LocationBar from './LocationBar';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getServiceOptions } from '../utils/serviceCatalog';
import { GLOBAL_ISSUE_EVENT } from '../utils/clientIssueReporter';

function HeaderIssueBanner() {
  const [issue, setIssue] = React.useState(null);

  React.useEffect(() => {
    const onIssue = (event) => {
      const detail = event.detail || {};
      setIssue({
        title: detail.title || 'We hit a small issue',
        message: detail.message || 'Something went wrong. Our team is already checking it. Please try again in a moment.',
        severity: detail.severity || 'medium',
        fingerprint: detail.fingerprint || null,
      });
    };
    window.addEventListener(GLOBAL_ISSUE_EVENT, onIssue);
    return () => window.removeEventListener(GLOBAL_ISSUE_EVENT, onIssue);
  }, []);

  React.useEffect(() => {
    if (!issue) return undefined;
    const timer = window.setTimeout(() => setIssue(null), 9000);
    return () => window.clearTimeout(timer);
  }, [issue]);

  if (!issue) return null;

  return (
    <div className={`header-issue-banner issue-${issue.severity}`} role="status" aria-live="polite">
      <div className="issue-mark">!</div>
      <div className="issue-copy">
        <strong>{issue.title}</strong>
        <span>{issue.message}</span>
      </div>
      <button type="button" onClick={() => setIssue(null)} aria-label="Dismiss issue message">
        Close
      </button>
    </div>
  );
}

export default function Header() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  // Consume the shared auth state — no duplicate Firestore reads
  const { user, isAdmin, isSuperAdmin, isRegionLead, isMason, isWorker, adminRole } = useAuth();

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [serviceQuery, setServiceQuery] = React.useState('');
  const [searchOpen, setSearchOpen] = React.useState(false);
  const services = React.useMemo(() => getServiceOptions(), []);
  const serviceMatches = React.useMemo(() => {
    const query = serviceQuery.trim().toLowerCase();
    if (!query) return services.slice(0, 6);
    return services
      .filter((service) =>
        service.name.toLowerCase().includes(query) ||
        service.category.toLowerCase().includes(query) ||
        service.iconLabel.toLowerCase().includes(query)
      )
      .slice(0, 7);
  }, [serviceQuery, services]);


  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // Sign-out failed
    }
    navigate('/');
    setMenuOpen(false);
  };

  const openService = (serviceName) => {
    setServiceQuery('');
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(`/service?type=${encodeURIComponent(serviceName)}`);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (serviceMatches[0]) openService(serviceMatches[0].name);
  };

  return (
    <>
    <header className="premium-header">
      {/* Logo */}
      <Link to="/" className="header-logo">
        <span className="logo-text">Gigtos</span>
      </Link>

      <form className="header-search" onSubmit={handleSearchSubmit}>
        <input
          value={serviceQuery}
          onFocus={() => setSearchOpen(true)}
          onChange={(event) => {
            setServiceQuery(event.target.value);
            setSearchOpen(true);
          }}
          placeholder="Search maid, cleaning, electrician..."
          aria-label="Search services"
        />
        <button type="submit">Search</button>
        {searchOpen && (
          <div className="header-search-results">
            {serviceMatches.length > 0 ? serviceMatches.map((service) => (
              <button key={service.id} type="button" onMouseDown={() => openService(service.name)}>
                <span>{service.name}</span>
                <small>{service.category}</small>
              </button>
            )) : (
              <div className="header-search-empty">No service found</div>
            )}
          </div>
        )}
      </form>
      <div className="header-actions">
        <ThemeToggle />

        {user ? (
          <>
            {/* Desktop Quick Nav — worker-specific vs consumer */}
            <nav className="desktop-nav">
              {isWorker ? (
                <>
                  <Link to="/worker/dashboard" className="nav-link">Dashboard</Link>
                  <Link to="/worker/open-work" className="nav-link">Open Jobs</Link>
                  <Link to="/worker/future-work" className="nav-link">Upcoming</Link>
                  <Link to="/worker/history" className="nav-link">History</Link>
                  <Link to="/worker/profile" className="nav-link">My Profile</Link>
                  <Link to="/worker/support" className="nav-link">Support</Link>
                      <Link to="/help" className="nav-link">Help</Link>
                  <Link to="/worker/dashboard" className="role-pill worker-role">👷 Worker</Link>
                </>
              ) : (
                <>
                  <Link to="/services" className="nav-link">Find Services</Link>
                  <Link to="/workers" className="nav-link">Join Workers</Link>
                  <Link to="/#how-it-works" className="nav-link">How it Works</Link>
                      <Link to="/help" className="nav-link">Help</Link>
                  <Link to="/profile" className="nav-link">Verify Identity</Link>
                  <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                  {isSuperAdmin ? (
                    <Link to="/admin/super" className="role-pill super-admin">🛡️ SuperAdmin</Link>
                  ) : adminRole === 'field_operator' ? (
                    <Link to="/operator" className="role-pill admin-role">Field Operator</Link>
                  ) : isRegionLead ? (
                    <Link to="/admin/region-lead" className="role-pill region-lead">📍 Region Lead</Link>
                  ) : isMason ? (
                    <Link to="/mason/dashboard" className="role-pill mason-role">🧱 Mason</Link>
                  ) : isAdmin ? (
                    <Link to="/admin" className="role-pill admin-role">👨‍💼 Admin</Link>
                  ) : (
                    <Link to="/my-bookings" className="nav-link">My Bookings</Link>
                  )}
                </>
              )}
            </nav>

            {/* Hamburger Menu Trigger */}
            <div className="menu-container">
              <button
                className={`menu-trigger ${menuOpen ? 'open' : ''}`}
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle Navigation"
              >
                {menuOpen ? '✕' : '☰'}
              </button>

              {menuOpen && (
                <div className="dropdown-menu">
                  <div className="menu-header">
                    <span className="user-email">👤 {user.email?.split('@')[0]}</span>
                  </div>

                  <div className="mobile-only-links">
                    <Link to="/" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">🏠 Home</Link>
                    {isWorker ? (
                      <>
                        <Link to="/worker/open-work" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">📋 Open Jobs</Link>
                        <Link to="/worker/future-work" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">📅 Upcoming</Link>
                        <Link to="/worker/history" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">🕐 History</Link>
                        <Link to="/worker/map" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">🗺️ Map</Link>
                        <Link to="/worker/support" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">💬 Support</Link>
                        <Link to="/help" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">❓ Help</Link>
                        <Link to="/worker/dashboard" onClick={() => setMenuOpen(false)} className="menu-item mobile-item highlighted">👷 Worker Dashboard</Link>
                      </>
                    ) : (
                      <>
                        <Link to="/services" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">Services</Link>
                        <Link to="/workers" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">Join Workers</Link>
                        <Link to="/jobs" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">💼 Browse Jobs</Link>
                        <Link to="/help" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">❓ Help</Link>
                        {(isAdmin || isSuperAdmin || isRegionLead) ? (
                          <>
                            <Link to="/my-bookings" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">📅 My Bookings</Link>
                            <Link to={isSuperAdmin ? '/admin/super' : adminRole === 'field_operator' ? '/operator' : isRegionLead ? '/admin/region-lead' : isMason ? '/mason/dashboard' : '/admin'} onClick={() => setMenuOpen(false)} className="menu-item mobile-item highlighted">Dashboard</Link>
                          </>
                        ) : (
                          <Link to="/my-bookings" onClick={() => setMenuOpen(false)} className="menu-item mobile-item">📅 My Bookings</Link>
                        )}
                      </>
                    )}
                  </div>

                  <Link to="/profile" onClick={() => setMenuOpen(false)} className="menu-item">✏️ Edit Profile</Link>
                  <div onClick={handleLogout} className="menu-item logout">🚪 Logout</div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <Link to="/workers" className="public-worker-link">Join Workers</Link>
            <button className="login-btn" onClick={() => navigate('/auth')}>Sign In</button>
          </>
        )}
      </div>

      <style>{`
        .premium-header {
          padding: 18px 48px;
          background: var(--bg-surface);
          backdrop-filter: none;
          border-bottom: 1px solid var(--border-light);
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 1000;
          box-shadow: none;
          transition: all var(--motion-base);
        }

        .header-issue-banner {
          margin: 0;
          padding: 12px 48px;
          background: linear-gradient(90deg, rgba(31, 17, 68, 0.98), rgba(83, 52, 131, 0.96));
          color: white;
          display: grid;
          grid-template-columns: 34px minmax(0, 1fr) auto;
          align-items: center;
          gap: 14px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.16);
          box-shadow: 0 16px 36px rgba(31, 17, 68, 0.18);
          position: sticky;
          top: 72px;
          z-index: 999;
        }

        .issue-mark {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: grid;
          place-items: center;
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.22);
          font-weight: 900;
          font-size: 18px;
        }

        .issue-copy {
          min-width: 0;
          display: grid;
          gap: 2px;
        }

        .issue-copy strong {
          font-size: 14px;
          font-weight: 900;
          line-height: 1.2;
        }

        .issue-copy span {
          font-size: 13px;
          line-height: 1.35;
          color: rgba(255, 255, 255, 0.84);
        }

        .header-issue-banner button {
          border: 1px solid rgba(255, 255, 255, 0.28);
          background: rgba(255, 255, 255, 0.12);
          color: white;
          border-radius: 7px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
        }

        .header-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }

        .logo-text {
          font-weight: 800;
          font-size: 30px;
          color: var(--primary-purple);
          letter-spacing: 0;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .header-search {
          flex: 1;
          max-width: 560px;
          min-width: 260px;
          display: flex;
          align-items: center;
          gap: 8px;
          position: relative;
          background: var(--bg-surface);
          border: 1px solid var(--border-light);
          border-radius: 999px;
          padding: 6px;
          box-shadow: var(--shadow-sm);
        }

        .header-search input {
          flex: 1;
          min-width: 0;
          border: none;
          outline: none;
          background: transparent;
          color: var(--text-main);
          font-size: 14px;
          padding: 8px 10px;
        }

        .header-search button[type="submit"] {
          border: none;
          border-radius: 7px;
          background: var(--primary-purple);
          color: white;
          font-size: 12px;
          font-weight: 800;
          padding: 8px 12px;
          cursor: pointer;
        }

        .header-search-results {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: var(--bg-surface);
          border: 1px solid var(--border-light);
          border-radius: 8px;
          box-shadow: var(--shadow-lg);
          overflow: hidden;
          z-index: 1200;
        }

        .header-search-results button {
          width: 100%;
          border: none;
          background: transparent;
          color: var(--text-main);
          padding: 12px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          border-bottom: 1px solid var(--border-light);
        }

        .header-search-results button:hover {
          background: var(--bg-soft);
        }

        .header-search-results small,
        .header-search-empty {
          color: var(--text-muted);
          font-size: 12px;
        }

        .header-search-empty {
          padding: 14px;
        }

        .desktop-nav {
          display: flex;
          gap: 20px;
          align-items: center;
        }

        .nav-link {
          color: var(--text-main);
          font-size: 16px;
          font-weight: 600;
          opacity: 0.8;
          transition: opacity 0.2s;
        }

        .nav-link:hover {
          opacity: 1;
          color: var(--primary-purple);
        }

        .role-pill {
          padding: 6px 14px;
          border-radius: var(--radius-pill);
          font-size: 13px;
          font-weight: 700;
          text-decoration: none;
          transition: transform 0.2s;
          border: 1px solid transparent;
        }

        .role-pill:hover {
          transform: translateY(-1px);
        }

        .role-pill.super-admin {
          background: var(--warning-bg);
          color: var(--warning);
          border-color: var(--warning);
        }

        .role-pill.region-lead {
          background: var(--primary-purple-glow);
          color: var(--primary-purple);
          border-color: var(--primary-purple);
        }

        .role-pill.mason-role {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          border-color: #10b981;
        }

        .role-pill.admin-role {
          background: var(--bg-soft);
          color: var(--text-muted);
          border-color: var(--border-light);
        }

        .role-pill.worker-role {
          background: var(--bg-soft);
          color: var(--primary-purple);
          border-color: var(--primary-purple);
        }

        .menu-trigger {
          background: var(--bg-soft);
          border: 1px solid var(--border-light);
          color: var(--text-main);
          width: 40px;
          height: 40px;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 20px;
          transition: all 0.2s;
        }

        .menu-trigger:hover {
          background: var(--primary-purple-glow);
          border-color: var(--primary-purple);
        }

        .dropdown-menu {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          min-width: 240px;
          background: var(--glass-bg);
          backdrop-filter: var(--glass-blur);
          border: 1px solid var(--glass-border);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          overflow: hidden;
          animation: slide-in 0.2s ease-out;
        }

        @keyframes slide-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .menu-header {
          padding: 16px;
          background: var(--bg-mesh-1);
          border-bottom: 1px solid var(--border-light);
        }

        .user-email {
          font-size: 13px;
          font-weight: 700;
          color: var(--text-main);
        }

        .menu-item {
          display: block;
          padding: 12px 16px;
          color: var(--text-main);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.2s;
        }

        .menu-item:hover {
          background: var(--primary-purple-glow);
          color: var(--primary-purple);
        }

        .menu-item.logout {
          color: var(--error);
          font-weight: 700;
          border-top: 1px solid var(--border-light);
          cursor: pointer;
        }

        .menu-item.highlighted {
          background: var(--primary-purple-glow);
          color: var(--primary-purple);
          font-weight: 700;
        }

        .login-btn {
          padding: 8px 20px;
          background: var(--primary-purple);
          color: white;
          border: none;
          border-radius: var(--radius-pill);
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .login-btn:hover {
          transform: translateY(-1px);
        }

        .public-worker-link {
          color: var(--text-main);
          font-size: 14px;
          font-weight: 800;
          text-decoration: none;
          white-space: nowrap;
        }

        .public-worker-link:hover {
          color: var(--primary-purple);
        }

        .mobile-only-links {
          display: none;
        }

        @media (max-width: 768px) {
          .desktop-nav { display: none; }
          .mobile-only-links { display: block; }
          .premium-header {
            padding: 10px 14px;
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 10px;
          }
          .header-issue-banner {
            top: 0;
            grid-template-columns: 30px minmax(0, 1fr) auto;
            padding: 10px 14px;
            gap: 10px;
          }
          .issue-mark {
            width: 30px;
            height: 30px;
          }
          .header-issue-banner button {
            padding: 7px 9px;
          }
          .header-search {
            order: 3;
            grid-column: 1 / -1;
            min-width: 0;
            max-width: none;
          }
          .header-actions {
            gap: 10px;
            justify-content: flex-end;
          }
          .public-worker-link {
            display: none;
          }
        }
      `}</style>
    </header>
    <HeaderIssueBanner />
    <LocationBar />
    </>
  );
}
